// /launch: the launch form, the live "Launch review" and the launch on pump.fun.
// The server builds two transactions (create + your first buy; then the fee split, fixed 70% to you and 30% to
// the coin's pool, locked so nobody can change it); the wallet signs both at once and the server sends them.
import { api } from "../lib/api.js";
import { config, configError, explorerTx } from "../lib/config.js";
import { esc, formatSol, toBig } from "../lib/format.js";
import { boot } from "../lib/page.js";
import { toast } from "../lib/toast.js";
import { $, coinLogo, ICONS, isSafeImage, navigate, note } from "../lib/ui.js";
import { buyPresets, cleanTicker, firstError, initialBuyLamports, isHttpUrl, LIMITS, MAX_UPLOAD_BYTES, normaliseLinks, UPLOAD_TYPES, validateLaunch } from "../lib/validate.js";
import { connect, friendlyError, hasWallet, NO_WALLET_MESSAGE, onWallet, state as wallet, signAllTransactions } from "../lib/wallet.js";

const F = {
  form: $("#launchForm"),
  name: $("#name"),
  symbol: $("#symbol"),
  logo: $("#logo"),
  description: $("#description"),
  twitter: $("#link-x"),
  telegram: $("#link-telegram"),
  website: $("#link-website"),
  custom: $("#customBuy"),
  file: $("#artwork"),
  button: $("#launchBtn"),
};
const INPUT_OF = { name: F.name, symbol: F.symbol, logo: F.logo, description: F.description, twitter: F.twitter, telegram: F.telegram, website: F.website, buy: F.custom };
const LINK_FIELDS = new Set(["twitter", "telegram", "website"]);

const S = {
  touched: new Set(),
  submitted: false,
  buyChoice: "0",
  presets: buyPresets(null),
  feeLamports: 25_000_000n, // network fees + account rent, about 0.025 SOL
  upload: { status: "idle", preview: null, url: null },
  uploadSeq: 0,
  tx: { status: "idle" },
  busy: false,
  done: false,
};

function values() {
  return {
    name: F.name.value,
    symbol: F.symbol.value,
    logo: F.logo.value.trim(),
    description: F.description.value,
    twitter: F.twitter.value,
    telegram: F.telegram.value,
    website: F.website.value,
    buyChoice: S.buyChoice,
    buyCustom: F.custom.value,
  };
}
const buyLamports = () => initialBuyLamports(S.buyChoice, F.custom.value, config());

/* ---------- rendering ---------- */

function renderCounts() {
  for (const [key, limit] of [["name", LIMITS.name], ["symbol", LIMITS.symbol], ["description", LIMITS.description]]) {
    const len = INPUT_OF[key].value.length;
    const el = $(`#count-${key}`);
    el.textContent = `${len}/${limit}`;
    el.classList.toggle("over", len > limit);
  }
}

function renderErrors() {
  const errs = validateLaunch(values());
  for (const [key, msg] of Object.entries(errs)) {
    const show = !!msg && (S.submitted || S.touched.has(key));
    const el = $(`#err-${key}`);
    if (el) {
      el.hidden = !show;
      el.textContent = show ? msg : "";
    }
    INPUT_OF[key]?.setAttribute("aria-invalid", show ? "true" : "false");
    if (show && LINK_FIELDS.has(key)) $("#links").open = true;
  }
  return errs;
}

function renderBuyOptions() {
  const box = $("#buyOptions");
  box.innerHTML = S.presets.options
    .map((o) => `<button type="button" aria-pressed="${o.value === S.buyChoice}" data-value="${esc(o.value)}" data-testid="launch-buy-${esc(o.value)}">${esc(o.label)}</button>`)
    .join("");
  $("#buySub").textContent = S.presets.mode === "percent"
    ? "Buy a share of your own coin in the launch transaction."
    : "Buy some of your own coin in the launch transaction. Amounts in SOL.";
  $("#customWrap").hidden = S.buyChoice !== "custom";
}

function renderBuyHelp() {
  const wei = buyLamports();
  let text;
  if (S.buyChoice === "0") text = "No initial buy. You can still trade on pump.fun after launch.";
  else if (S.buyChoice === "custom") text = wei ? `${formatSol(wei)} SOL goes into your first buy, in the launch transaction, so nobody can buy before you.` : "Enter how much SOL to put into your first buy.";
  else if (S.presets.mode === "percent") text = `About ${formatSol(wei)} SOL for ${S.buyChoice}% of the supply, estimated from the bonding curve. You get whatever that SOL buys at launch.`;
  else text = `${formatSol(wei)} SOL goes into your first buy, in the launch transaction, so nobody can buy before you.`;
  $("#buyHelp").textContent = text;
}

function previewSrc() {
  if (S.upload.preview) return S.upload.preview;
  const logo = F.logo.value.trim();
  return isHttpUrl(logo) && isSafeImage(logo) ? logo : "";
}

function renderReview() {
  if (S.done) return;
  const v = values();
  const name = v.name.trim();
  $("#reviewName").textContent = name || "Your coin";
  $("#reviewSymbol").textContent = `$${v.symbol || "TICKER"}`;
  $("#reviewLogo").innerHTML = coinLogo(previewSrc(), v.symbol || "COIN", 48, name);
  // The fee split is fixed (70 / 30) and shown as written in launch.html.
  const buy = buyLamports() ?? 0n;
  $("#reviewFee").textContent = `~${formatSol(S.feeLamports)} SOL`;
  $("#reviewBuy").textContent = `${formatSol(buy)} SOL`;
  $("#reviewUpfront").textContent = formatSol(S.feeLamports + buy);
}

function renderAction() {
  if (S.done) return;
  const b = F.button;
  const cfg = config();
  if (S.busy) {
    b.disabled = true;
    b.classList.add("busy");
    b.textContent = S.tx.status === "pending" ? "Launching…" : "Waiting for wallet…";
    return;
  }
  b.classList.remove("busy");
  if (!wallet.address) {
    b.textContent = "Connect wallet";
    b.disabled = false;
  } else {
    b.textContent = "Launch coin";
    b.disabled = cfg.ready === false || S.upload.status === "uploading";
  }
}

function setTx(s) {
  S.tx = s;
  const box = $("#txStatus");
  const spin = '<span class="spin" aria-hidden="true"></span>';
  if (s.status === "checking") box.innerHTML = note("", `${spin}<span>Checking the launch…</span>`);
  else if (s.status === "wallet") box.innerHTML = note("", `${spin}<span>Confirm the launch in your wallet (one approval for both steps).</span>`);
  else if (s.status === "pending") box.innerHTML = note("", `${spin}<span>Launching on pump.fun and locking the fee split…</span>`);
  else if (s.status === "setup") box.innerHTML = note("err", `${esc(s.message)} <button type="button" class="btn primary sm" id="finishBtn" data-testid="launch-finish">Finish setup</button>`);
  else if (s.status === "error") box.innerHTML = note("err", esc(s.message));
  else box.innerHTML = "";
  renderAction();
}

function renderArt() {
  const { status, preview } = S.upload;
  const img = $("#artPreview");
  img.hidden = !preview;
  if (preview) img.src = preview;
  else img.removeAttribute("src");
  $("#dropHint").hidden = !!preview;
  $("#artBusy").hidden = status !== "uploading";
  $("#artClear").hidden = !preview || status === "uploading";
}

function renderConfigNote() {
  const el = $("#configError");
  if (configError()) {
    el.textContent = "Couldn't load the launch settings from the Shilled server, so launching is paused. Reload the page to try again.";
    el.hidden = false;
  } else if (config().ready === false) {
    el.textContent = "Launching opens in a moment: the server is still getting ready. The form below shows what you'll be able to launch.";
    el.hidden = false;
  } else el.hidden = true;
}

function renderAll() {
  renderCounts();
  renderBuyOptions();
  renderBuyHelp();
  renderErrors();
  renderArt();
  renderReview();
  renderAction();
}

/* ---------- artwork upload ---------- */

const readAsDataUrl = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result));
  r.onerror = () => reject(r.error);
  r.readAsDataURL(file);
});

function artError(msg) {
  const el = $("#err-artwork");
  el.textContent = msg;
  el.hidden = !msg;
}

async function onFile(file) {
  artError("");
  if (!file) return;
  if (!UPLOAD_TYPES.includes(file.type)) { artError("Use a PNG, JPG or WebP image."); return; }
  if (file.size > MAX_UPLOAD_BYTES) { artError("That image is larger than 4 MB. Use a smaller PNG, JPG or WebP."); return; }
  const seq = ++S.uploadSeq;
  let preview = null;
  try { preview = await readAsDataUrl(file); } catch { preview = null; }
  S.upload = { status: "uploading", preview, url: null };
  renderArt(); renderReview(); renderAction();
  try {
    const r = await api.upload(file);
    if (seq !== S.uploadSeq) return;
    if (!r || !r.url) throw new Error("the server didn't return an image link");
    const abs = new URL(r.url, location.origin).href;
    F.logo.value = abs;
    S.upload = { status: "done", preview, url: abs };
  } catch (e) {
    if (seq !== S.uploadSeq) return;
    S.upload = { status: "idle", preview: null, url: null };
    artError(`Couldn't upload the image: ${String(e.message || e).replace(/\.$/, "")}. Try again, or paste a logo link below.`);
  }
  try { F.file.value = ""; } catch { /* some browsers refuse */ }
  renderArt(); renderErrors(); renderReview(); renderAction();
}

function clearArt() {
  S.uploadSeq++;
  if (S.upload.url && F.logo.value.trim() === S.upload.url) F.logo.value = "";
  S.upload = { status: "idle", preview: null, url: null };
  artError("");
  renderArt(); renderErrors(); renderReview(); renderAction();
}

/* ---------- launch ---------- */

function focusField(key) {
  if (LINK_FIELDS.has(key)) $("#links").open = true;
  const input = INPUT_OF[key];
  if (input && typeof input.focus === "function") input.focus();
}

function showSuccess({ hash, token, name, symbol }) {
  S.done = true;
  const review = $("#review");
  review.innerHTML = `
    <div class="success-state" data-testid="launch-success">
      <span class="success-check">${ICONS.check}</span>
      <h2 class="card-title">${token ? `${esc(name)} is live.` : "Launch confirmed."}</h2>
      <p class="card-sub">${token ? `$${esc(symbol)} is live on pump.fun with its shiller pool open. Taking you to its page…` : "Your coin will show up in Explore within a minute."}</p>
      <div class="stack-sm" style="width:100%;margin-top:8px">
        ${token ? `<a class="btn primary lg block" href="/coin/${esc(token)}" data-testid="launch-success-link">Open coin page</a>` : '<a class="btn primary lg block" href="/explore">Explore coins</a>'}
        ${token ? `<a class="btn ghost block" href="https://pump.fun/coin/${esc(token)}" target="_blank" rel="noopener noreferrer" data-testid="launch-success-pump">View on pump.fun</a>` : ""}
      </div>
    </div>`;
  F.form.querySelectorAll("input, textarea, button").forEach((el) => { el.disabled = true; });
}

async function onSubmit(e) {
  e.preventDefault();
  if (S.busy || S.done) return;
  S.submitted = true;
  const errs = renderErrors();
  const first = firstError(errs);
  if (S.upload.status === "uploading") { setTx({ status: "error", message: "Wait for the artwork to finish uploading." }); return; }
  if (first) {
    setTx({ status: "idle" });
    toast("Check the highlighted fields.");
    focusField(first[0]);
    return;
  }
  if (!hasWallet()) { setTx({ status: "error", message: NO_WALLET_MESSAGE }); return; }
  if (!wallet.address) {
    try { await connect(); setTx({ status: "idle" }); } catch (err) { setTx({ status: "error", message: friendlyError(err) }); }
    return;
  }
  const cfg = config();
  if (cfg.ready === false) { renderConfigNote(); setTx({ status: "error", message: "Launching isn't open yet: the server is still getting ready. Try again in a minute." }); return; }

  const v = values();
  const buy = buyLamports() ?? 0n;
  const links = normaliseLinks(v);
  S.busy = true;
  setTx({ status: "checking" });
  try {
    const prep = await api.launchPrepare({
      wallet: wallet.address, name: v.name.trim(), symbol: v.symbol, logo: v.logo, description: v.description.trim(),
      ...links, initialBuySol: (Number(buy) / 1e9).toString(),
    });
    setTx({ status: "wallet" });
    const signed = await signAllTransactions(prep.transactions);
    setTx({ status: "pending" });
    const r = await api.launchSubmit({ id: prep.id, mint: prep.mint, wallet: wallet.address, signed });
    S.busy = false;
    if (!r.ok) { S.pendingMint = r.token; setTx({ status: "setup", message: r.error || "The coin was created, but its fee split isn't set yet." }); wireFinish(); return; }
    setTx({ status: "idle" });
    showSuccess({ hash: null, token: r.token, name: v.name.trim(), symbol: v.symbol });
    toast(`${v.name.trim()} is live.`);
    setTimeout(() => navigate(`/coin/${r.token}?launched=1`), 1600);
  } catch (err) {
    S.busy = false;
    setTx({ status: "error", message: friendlyError(err) });
  }
}

/** Create landed but the fee split didn't: sign the split again (the coin isn't listed until it's set). */
function wireFinish() {
  const btn = $("#finishBtn");
  if (!btn) return;
  btn.onclick = async () => {
    btn.disabled = true;
    try {
      const prep = await api.launchFinish({ wallet: wallet.address, mint: S.pendingMint });
      const signed = await signAllTransactions(prep.transactions);
      setTx({ status: "pending" });
      const r = await api.launchFinish({ wallet: wallet.address, mint: S.pendingMint, id: prep.id, signed });
      if (!r.ok) throw new Error("The fee split still didn't land. Try again in a minute.");
      setTx({ status: "idle" });
      showSuccess({ hash: null, token: r.token, name: values().name.trim(), symbol: values().symbol });
      setTimeout(() => navigate(`/coin/${r.token}?launched=1`), 1600);
    } catch (err) { setTx({ status: "setup", message: friendlyError(err) }); wireFinish(); }
  };
}

/* ---------- wiring ---------- */

function wire() {
  for (const key of ["name", "symbol", "logo", "description", "twitter", "telegram", "website"]) {
    const input = INPUT_OF[key];
    input.addEventListener("input", () => {
      if (key === "symbol") {
        const clean = cleanTicker(input.value);
        if (clean !== input.value) input.value = clean;
      }
      if (key === "logo" && S.upload.url && input.value.trim() !== S.upload.url) S.upload = { status: "idle", preview: null, url: null };
      if ((key === "name" || key === "description") && input.value.length > LIMITS[key]) S.touched.add(key);
      renderCounts(); renderErrors(); renderReview(); renderArt();
      if (S.tx.status === "error") setTx({ status: "idle" });
    });
    input.addEventListener("blur", () => { if (input.value) S.touched.add(key); renderErrors(); });
  }
  $("#buyOptions").addEventListener("click", (e) => {
    const b = e.target.closest("button[data-value]");
    if (!b || S.done) return;
    S.buyChoice = b.dataset.value;
    renderBuyOptions(); renderBuyHelp(); renderErrors(); renderReview();
    if (S.buyChoice === "custom") F.custom.focus();
  });
  F.custom.addEventListener("input", () => {
    const clean = F.custom.value.replace(/,/g, ".").replace(/[^0-9.]/g, "");
    if (clean !== F.custom.value) F.custom.value = clean;
    renderBuyHelp(); renderErrors(); renderReview();
  });
  F.custom.addEventListener("blur", () => { S.touched.add("buy"); renderErrors(); });

  F.file.addEventListener("change", () => onFile(F.file.files && F.file.files[0]));
  $("#artClear").addEventListener("click", (e) => { e.preventDefault(); clearArt(); });
  const dz = $("#dropzone");
  ["dragenter", "dragover"].forEach((t) => dz.addEventListener(t, (e) => { e.preventDefault(); dz.classList.add("drag"); }));
  ["dragleave", "dragend"].forEach((t) => dz.addEventListener(t, () => dz.classList.remove("drag")));
  dz.addEventListener("drop", (e) => {
    e.preventDefault();
    dz.classList.remove("drag");
    const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) onFile(file);
  });

  F.form.addEventListener("submit", onSubmit);
  onWallet(renderAction);
}

async function main() {
  wire();
  renderAll();
  const cfg = await boot();
  S.presets = buyPresets(cfg);
  if (!S.presets.options.some((o) => o.value === S.buyChoice)) S.buyChoice = "0";
  renderConfigNote();
  renderAll();
}

main().catch((e) => console.error(e));
